// The game's pictures: standalone sprites (Content/Sprites), named 16px tileset regions (Content/Tilesets),
// the generated atlases (characters,
// armour layers, UI skin; see atlas.json), the chat emoji (emoji.json) and the font for text in the world. Content names its sprites by path
// ("Sprites/axe.png"), the page's own code by name ("loot_bag"): both work wherever a sprite is looked up.

import { CanvasSource, ImageSource, Rectangle, Texture, TextureSource } from 'pixi.js';
import { textResolution } from './ui/scale';
import { assetUrl } from './assetUrl';
import { parseTileset } from './core/tilesets';

export const CELL = 16;

/** A sprite's name from what refers to it: a content path ("Sprites/axe.png") or a name ("axe"). */
export function spriteName(key: string): string {
  const slash = key.lastIndexOf('/');
  const name = slash >= 0 ? key.slice(slash + 1) : key;
  return name.endsWith('.png') ? name.slice(0, -4) : name;
}

interface Atlas {
  cellSize: number; columns: number;
  ui: Record<string, number>; armor: Record<string, number>;
  characters: { appearances: number; framesPerDirection: number; directionOrder: string[] };
}

async function loadImage(url: string): Promise<HTMLImageElement> {
  const img = new Image();
  img.src = assetUrl(url);
  await img.decode();
  return img;
}

function cell(base: Texture, x: number, y: number, w = CELL, h = CELL): Texture {
  return new Texture({ source: base.source, frame: new Rectangle(x, y, w, h) });
}

export class Sprites {
  /** Library sprites: name → 16x16 cells, frame by frame, each frame's cells left to right, top to bottom. */
  readonly cells = new Map<string, Texture[]>();
  readonly sizes = new Map<string, { w: number; h: number }>();
  readonly frameCount = new Map<string, number>();
  readonly images = new Map<string, HTMLImageElement>();
  missing!: Texture[];

  characters!: Texture;
  armor!: Texture;
  ui!: Texture;
  uiImage!: HTMLImageElement;
  atlas!: Atlas;
  font = new LabelFont();
  private readonly emojiCells = new Map<string, Texture>();

  /** A sprite's cells (by path or name), or the magenta placeholder. */
  get(key: string): Texture[] {
    return this.cells.get(spriteName(key)) ?? this.missing;
  }

  /** Cells of one frame (multi-tile sprites: w*h cells). */
  frame(key: string, frame: number): Texture[] {
    const cells = this.get(key);
    const size = this.sizes.get(spriteName(key)) ?? { w: 1, h: 1 };
    const n = size.w * size.h, frames = Math.max(1, Math.floor(cells.length / n));
    const f = ((frame % frames) + frames) % frames;
    return cells.slice(f * n, f * n + n);
  }

  /** A chat emoji (a name from emoji.json): smooth art, sampled linearly with mipmaps to scale to any text size. */
  emoji(name: string): Texture { return this.emojiCells.get(name) ?? this.missing[0]; }

  private uiCells = new Map<string, Texture>();
  uiCell(name: string): Texture {
    const found = this.uiCells.get(name); if (found) return found;
    const i = this.atlas.ui[name] ?? 0;
    const texture = cell(this.ui, (i % this.atlas.columns) * CELL, Math.floor(i / this.atlas.columns) * CELL);
    this.uiCells.set(name, texture); return texture;
  }

  /** A character frame: direction order down, left, right, up; 3 frames each; one row per appearance. */
  character(appearance: number, direction: number, frame: number): Texture {
    const n = this.atlas.characters.appearances;
    const a = ((appearance % n) + n) % n;
    return this.characterCells[a][direction * 3 + frame];
  }

  /** The worn layer of an armour look + slot ("bark_chest") for a character frame, or null. */
  armorLayer(lookSlot: string, direction: number, frame: number): Texture | null {
    const row = this.armorCells.get(lookSlot);
    return row ? row[direction * 3 + frame] : null;
  }

  private characterCells: Texture[][] = [];
  private armorCells = new Map<string, Texture[]>();

  static async load(spriteNames: string[], status: (s: string) => void, tilesetFiles: string[] = []): Promise<Sprites> {
    TextureSource.defaultOptions.scaleMode = 'nearest';
    const s = new Sprites();
    const tex = 'Content/Textures/';
    s.atlas = await (await fetch(assetUrl(tex + 'atlas.json'))).json();
    const sizes = await (await fetch(assetUrl('Content/Sprites/sprites.json'))).json().catch(() => ({}));
    for (const [name, v] of Object.entries(sizes as Record<string, { Width: number; Height: number }>)) s.sizes.set(name, { w: v.Width, h: v.Height });

    const [chars, armor, ui] = await Promise.all(['characters', 'armor', 'ui'].map(n => loadImage(`${tex}${n}.png`)));
    s.characters = Texture.from(chars);
    s.armor = Texture.from(armor);
    s.ui = Texture.from(ui);
    s.uiImage = ui;
    for (let a = 0; a < s.atlas.characters.appearances; a++)
      s.characterCells.push(Array.from({ length: 12 }, (_, i) => cell(s.characters, i * CELL, a * CELL)));
    for (const [key, row] of Object.entries(s.atlas.armor))
      s.armorCells.set(key, Array.from({ length: 12 }, (_, i) => cell(s.armor, i * CELL, row * CELL)));

    s.missing = [Texture.from(missingCell())];
    const [emojiSheet, emojiAtlas] = await Promise.all([loadImage(`${tex}emoji.png`),
      fetch(assetUrl(tex + 'emoji.json')).then(r => r.json() as Promise<{ cellSize: number; columns: number; emoji: Record<string, number> }>)]);
    const emojiBase = new Texture({ source: new ImageSource({ resource: emojiSheet, scaleMode: 'linear', mipmapFilter: 'linear', autoGenerateMipmaps: true }) });
    const e = emojiAtlas.cellSize;
    for (const [name, i] of Object.entries(emojiAtlas.emoji))
      s.emojiCells.set(name, cell(emojiBase, (i % emojiAtlas.columns) * e, Math.floor(i / emojiAtlas.columns) * e, e, e));
    const tilesets = await Promise.all(tilesetFiles.map(async path => {
      const response = await fetch(assetUrl(path));
      if (!response.ok) throw new Error(`Could not load tileset ${path}`);
      return parseTileset(await response.json());
    }));
    const registered = new Set(spriteNames);
    for (const tileset of tilesets) for (const region of tileset.tiles) {
      const name = spriteName(region.sprite);
      if (registered.has(name)) throw new Error(`Duplicate sprite/tileset region: ${name}`);
      registered.add(name);
    }
    // Each sheet is fetched/uploaded once; its logical sprites are views of the same GPU texture.
    const sheetImages = new Map<string, Promise<HTMLImageElement>>();
    const sheets = tilesets.map(async tileset => {
      let loading = sheetImages.get(tileset.image);
      if (!loading) { loading = loadImage(`Content/Tilesets/${tileset.image}`); sheetImages.set(tileset.image, loading); }
      const img = await loading;
      if (img.width !== tileset.columns * CELL || img.height !== tileset.rows * CELL) throw new Error(`Invalid tileset sheet size: ${tileset.image}`);
      const base = Texture.from(img);
      for (const region of tileset.tiles) {
        const name = spriteName(region.sprite);
        s.cells.set(name, [cell(base, region.column * CELL, region.row * CELL)]);
        s.frameCount.set(name, 1);
      }
    });
    let done = 0;
    await Promise.all([...sheets, ...spriteNames.map(async name => {
      try {
        const img = await loadImage(`Content/Sprites/${name}.png`);
        s.images.set(name, img);
        const size = s.sizes.get(name) ?? { w: 1, h: 1 };
        const fw = size.w * CELL, fh = size.h * CELL;
        if (img.height !== fh || img.width % fw !== 0) throw new Error(`${name}.png must be a strip of ${fw}x${fh} frames`);
        const base = Texture.from(img);
        const cells: Texture[] = [];
        for (let f = 0; f < img.width / fw; f++)
          for (let cy = 0; cy < size.h; cy++)
            for (let cx = 0; cx < size.w; cx++) cells.push(cell(base, f * fw + cx * CELL, cy * CELL));
        s.cells.set(name, cells);
        s.frameCount.set(name, img.width / fw);
      } catch (e) {
        console.warn(`Sprite ${name}:`, e);
      }
      status(`Loading pictures ${++done}/${spriteNames.length}`);
    })]);
    return s;
  }
}

function missingCell(): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = c.height = CELL;
  const g = c.getContext('2d')!;
  for (let y = 0; y < CELL; y += 4)
    for (let x = 0; x < CELL; x += 4) {
      g.fillStyle = ((x + y) / 4) % 2 === 0 ? '#ff00ff' : '#000';
      g.fillRect(x, y, 4, 4);
    }
  return c;
}

/**
 * Text drawn in the world (name tags, damage numbers, prompts): the UI's font (Fira Sans) supersampled at display density with a
 * thin dark outline, so it reads over any ground. Each string is rendered once per style into a small canvas and cached as
 * a texture, drawn unscaled at whole pixels.
 */
export class LabelFont {
  private readonly cache = new Map<string, Texture>();
  private readonly measurer = document.createElement('canvas').getContext('2d')!;

  /** Texture padding in screen pixels (kept independent of outline thickness for stable label placement). */
  static pad(size: number): number { return Math.max(2, Math.round(size / 6)); }

  /** The width of the text without padding, in pixels. */
  measure(text: string, size: number, weight = 500): number {
    this.measurer.font = `${weight} ${size}px 'Fira Sans', sans-serif`;
    return this.measurer.measureText(text).width;
  }

  /**
   * A texture of the text: size in pixels, weight 400-600. With `plate`, the text sits on a dark rounded plate drawn
   * into the same texture (prompts): one sprite, never a Graphics shape under a sprite, which some mobile GPUs (Mali)
   * drew over the text.
   */
  texture(text: string, color: string, size: number, weight = 500, outline = 'rgba(8,6,8,0.9)', plate = false): Texture {
    const resolution = textResolution();
    const key = `${resolution}|${color}|${size}|${weight}|${outline}|${plate}|${text}`;
    let t = this.cache.get(key);
    if (t) return t;
    const font = `${weight} ${size}px 'Fira Sans', sans-serif`;
    this.measurer.font = font;
    const pad = LabelFont.pad(size);
    const w = Math.ceil(this.measurer.measureText(text).width) + pad * 2, h = Math.ceil(size * 1.25) + pad * 2;
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.ceil(w * resolution));
    c.height = Math.ceil(h * resolution);
    const g = c.getContext('2d')!;
    g.scale(resolution, resolution);
    if (plate) {
      g.beginPath();
      g.roundRect(0.5, pad / 2 + 0.5, w - 1, h - pad - 1, pad * 2);
      g.fillStyle = 'rgba(12,10,14,0.7)';
      g.fill();
      g.lineWidth = 1;
      g.strokeStyle = 'rgba(201,154,80,0.6)';
      g.stroke();
    }
    g.font = font;
    g.textBaseline = 'middle';
    g.lineJoin = 'round';
    // Canvas strokes straddle the glyph edge: this leaves ~1px of outline at normal label sizes.
    g.lineWidth = Math.max(1.5, size / 7);
    g.strokeStyle = outline;
    g.strokeText(text, pad, h / 2 + 0.5);
    g.fillStyle = color;
    g.fillText(text, pad, h / 2 + 0.5);
    t = new Texture({ source: new CanvasSource({ resource: c, resolution, scaleMode: 'linear' }) });
    if (this.cache.size > 2000) { for (const old of this.cache.values()) old.destroy(true); this.cache.clear(); }
    this.cache.set(key, t);
    return t;
  }
}
